import { localStorage } from '@/infra/storage'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { type Id } from '@/infra/id'
import { memoize } from '@/utils/memoize-util'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { DefaultLastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import type { UserManagementFeatureOptions } from '@/features/user-management'
import { UserManagementViewModel } from '@/features/user-management/UserManagement/UserManagementViewModel'
import { UserDetailsViewModel } from '@/features/user-management/UserDetails/UserDetailsViewModel'
import { UserSearchViewModel } from '@/features/user-management/UserSearchViewModel'
import { WorkspaceInvitationDialogViewModel } from '@/features/user-management/WorkspaceInvitationDialog/WorkspaceInvitationDialogViewModel'
import { ManageUserCapacityDialogViewModel } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogViewModel'
import { EditUserRoleDialogViewModel } from '@/features/user-management/EditUserRoleDialog/EditUserRoleDialogViewModel'
import { ManageSkillsDialogViewModel } from '@/features/user-management/ManageSkillsDialog/ManageSkillsDialogViewModel'
import { ManageSkillsListDialogViewModel } from '@/features/user-management/ManageSkillsListDialog/ManageSkillsListDialogViewModel'
import { RemoveFromWorkspaceDialogViewModel } from '@/features/user-management/RemoveFromWorkspaceDialog/RemoveFromWorkspaceDialogViewModel'
import { RestoreToWorkspaceDialogViewModel } from '@/features/user-management/RestoreToWorkspaceDialog/RestoreToWorkspaceDialogViewModel'
import { PromoteToOwnerDialogViewModel } from '@/features/user-management/PromoteToOwnerDialog/PromoteToOwnerDialogViewModel'
import { AddUserToTeamDialogViewModel } from '@/features/user-management/AddUserToTeamDialog/AddUserToTeamDialogViewModel'
import { AddUserToWorkspaceDialogViewModel } from '@/features/user-management/AddUserToWorkspaceDialog/AddUserToWorkspaceDialogViewModel'
import { AddCustomerToWorkspaceDialogViewModel } from '@/features/user-management/AddCustomerToWorkspaceDialog/AddCustomerToWorkspaceDialogViewModel'
import { JoinWorkspaceAsAdminDialogViewModel } from '@/features/user-management/JoinWorkspaceAsAdminDialog/JoinWorkspaceAsAdminDialogViewModel'
import { EditUserInformationDialogViewModel } from '@/features/user-management/EditUserInformationDialog/EditUserInformationDialogViewModel'
import { EditProDetailsDialogViewModel } from '@/features/user-management/EditProDetailsDialog/EditProDetailsDialogViewModel'
import {
  type MembershipsTabDialogs,
  MembershipsTabViewModel,
} from '@/features/user-management/MembershipsTab/MembershipsTabViewModel'

/**
 * Function to acquire the view model for a user's details page.
 */
export type AcquireUserDetailsViewModelFn = (
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<UserDetailsViewModel>

export function InternalUserManagementFeature(
  options: UserManagementFeatureOptions,
) {
  const createRemoveFromWorkspaceDialogViewModel = (
    onRemoved: (userPublicIds: Id[]) => void,
  ) =>
    new RemoveFromWorkspaceDialogViewModel(
      onRemoved,
      options.updateMember,
      options.newAlert,
      options.createAlertErrorHandler,
    )

  const createRestoreToWorkspaceDialogViewModel = (
    onRestored?: (userPublicIds: Id[]) => void,
  ) =>
    new RestoreToWorkspaceDialogViewModel(
      onRestored,
      options.updateMember,
      options.newAlert,
      options.createAlertErrorHandler,
    )

  const createManageUserCapacityDialogViewModel = (onUpdated?: () => void) =>
    new ManageUserCapacityDialogViewModel(
      onUpdated,
      options.fetchMember,
      options.fetchUserData,
      options.updateMember,
      options.updateUserData,
      options.getCurrentMember,
      options.newAlert,
      options.createAlertErrorHandler,
      options.createAlertRetryWrapper,
    )

  const createEditUserRoleDialogViewModel = () =>
    new EditUserRoleDialogViewModel(
      options.listRoles,
      options.fetchMember,
      options.addMemberRole,
      options.newAlert,
      options.createAlertErrorHandler,
      options.createAlertRetryWrapper,
    )

  const userManagementViewModel: UserManagementViewModel =
    new UserManagementViewModel(
      options.getCurrentMember,
      localStorage,
      options.getCurrentUserWorkspaceMemberships,
      options.getWorkspaceSelection,
      new DefaultLastWorkspaceStorage(localStorage, 'userManagement'),
      new UserSearchViewModel(
        options.searchUsers,
        options.listRoles,
        options.createAlertRetryWrapper,
        (ns) => new FilterStorage(localStorage, ns),
        () => userManagementViewModel.workspaceSelector.setDefaultWorkspace(),
      ),
      (vmOptions) =>
        new WorkspaceInvitationDialogViewModel(
          vmOptions.selectedWorkspace,
          vmOptions.selectedUserIds,
          options.getSearchableUsers,
          options.getCurrentUserWorkspaceMemberships,
          options.listRoles,
          options.getWorkspaceRoles,
          options.listSkills,
          options.getWorkspaceSkills,
          options.listTeams,
          options.getWorkspaceTeams,
          options.listTeamRoles,
          options.getWorkspaceTeamRoles,
          options.createMemberManually,
          options.createCustomer,
          options.bulkCreateCustomers,
          options.searchMembersByPermission,
          options.newAlert,
          options.createAlertErrorHandler,
          options.createAlertRetryWrapper,
        ),
      (vmOptions) =>
        new ManageSkillsDialogViewModel(
          vmOptions.selectedWorkspace,
          vmOptions.selectedUserIds,
          vmOptions.users,
          vmOptions.toggleUser,
          options.listSkills,
          options.getWorkspaceSkills,
          options.addMemberSkills,
          options.removeMemberSkills,
          options.getCurrentUserWorkspaceMemberships,
          options.newAlert,
          options.createAlertRetryWrapper,
        ),
      createManageUserCapacityDialogViewModel(),
      createEditUserRoleDialogViewModel(),
      createRemoveFromWorkspaceDialogViewModel,
      createRestoreToWorkspaceDialogViewModel,
    )

  const acquireUserDetailsViewModel: AcquireUserDetailsViewModelFn = memoize(
    async (userPublicId: Id, signal?: AbortSignal) => {
      signal?.throwIfAborted()

      const {
        createUserJobsTabViewModel,
        createDocumentsTabViewModel,
        createProDetailsViewModel,
        createClientAssignmentViewModel,
        createAdvisorClientsTabViewModel,
      } = await ValueRetrievablePromise.props({
        createUserJobsTabViewModel: options.createUserJobsTabViewModel,
        createDocumentsTabViewModel: options.createDocumentsTabViewModel,
        createProDetailsViewModel: options.createProDetailsViewModel,
        createClientAssignmentViewModel:
          options.createClientAssignmentViewModel,
        createAdvisorClientsTabViewModel:
          options.createAdvisorClientsTabViewModel,
      })
      signal?.throwIfAborted()

      return new UserDetailsViewModel(
        userPublicId,
        options.fetchSearchableUser,
        options.getCurrentMember,
        options.getCurrentUserWorkspaceMemberships,
        options.primeProvider,
        options.fetchBillingProfile,
        options.fetchPayoutMethodInfo,
        options.fetchJobTotals,
        options.promptCreateJob,
        createUserJobsTabViewModel,
        createDocumentsTabViewModel,
        createClientAssignmentViewModel,
        createAdvisorClientsTabViewModel,
        () => userManagementViewModel.canAccessUserManagement,
        createEditUserRoleDialogViewModel(),
        createRestoreToWorkspaceDialogViewModel(),
        new EditProDetailsDialogViewModel(createProDetailsViewModel),
        new ManageSkillsListDialogViewModel(
          options.listSkills,
          options.getWorkspaceSkills,
          options.fetchMember,
          options.addMemberSkills,
          options.removeMemberSkills,
          options.newAlert,
          options.createAlertErrorHandler,
          options.createAlertRetryWrapper,
        ),
        createRemoveFromWorkspaceDialogViewModel,
        createManageUserCapacityDialogViewModel,
        (onPromoted) =>
          new PromoteToOwnerDialogViewModel(
            onPromoted,
            options.promoteToOwner,
            options.newAlert,
            options.createAlertErrorHandler,
          ),
        (user) =>
          new AddUserToTeamDialogViewModel(
            user,
            options.listTeams,
            options.getWorkspaceTeams,
            options.listTeamRoles,
            options.getWorkspaceTeamRoles,
            options.listTeamMembersForWorkspaceMember,
            options.addTeamMember,
            options.getCurrentUserWorkspaceMemberships,
            options.newAlert,
            options.createAlertErrorHandler,
            options.createAlertRetryWrapper,
          ),
        (user, onAdded) =>
          new AddUserToWorkspaceDialogViewModel(
            user,
            onAdded,
            options.listRoles,
            options.getWorkspaceRoles,
            options.listSkills,
            options.getWorkspaceSkills,
            options.listTeams,
            options.getWorkspaceTeams,
            options.listTeamRoles,
            options.getWorkspaceTeamRoles,
            options.createMember,
            options.getCurrentUserWorkspaceMemberships,
            options.newAlert,
            options.createAlertErrorHandler,
            options.createAlertRetryWrapper,
          ),
        (user, onAdded) =>
          new AddCustomerToWorkspaceDialogViewModel(
            user,
            onAdded,
            options.createCustomer,
            options.getCurrentUserWorkspaceMemberships,
            options.newAlert,
            options.createAlertErrorHandler,
          ),
        (userMembershipWorkspaceIds, onJoined) =>
          new JoinWorkspaceAsAdminDialogViewModel(
            userMembershipWorkspaceIds,
            onJoined,
            options.listWorkspaces,
            options.joinWorkspaceAsAdmin,
            options.newAlert,
            options.createAlertErrorHandler,
          ),
        (onSaved) =>
          new EditUserInformationDialogViewModel(
            onSaved,
            options.updateMember,
            options.updateUserData,
            options.getCurrentMember,
            options.newAlert,
            options.createAlertErrorHandler,
          ),
        (
          user: () => SearchableUser,
          dialogs: MembershipsTabDialogs,
          canJoinWorkspaceAsAdmin: () => boolean,
          canPromoteToOwner: () => boolean,
        ) =>
          new MembershipsTabViewModel(
            user,
            dialogs,
            canJoinWorkspaceAsAdmin,
            canPromoteToOwner,
            options.listUserTeamMembersForWorkspace,
            options.impersonateMember,
            options.generateMemberMagicLink,
            options.fetchMemberCoupons,
            options.fetchJobTotalStatusAggregations,
            options.getCurrentMember,
            options.newAlert,
            options.createAlertErrorHandler,
            options.createAlertRetryWrapper,
          ),
      )
    },
    {
      // Only the open user's page state is kept, so a revalidating route
      // reuses it while switching users starts afresh.
      maxCount: 1,
      promise: ([, signal]) => signal,
    },
  )

  return {
    // Internal
    userManagementViewModel,
    acquireUserDetailsViewModel,
  }
}
